/**
 * Editor insert-flow + bbox geometry — Cluster G of editor.ts (Phase EB.4).
 *
 * Extracted from editor.ts in Phase EB.4 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §"Phase EB.4"
 * + §9 cross-cluster dependencies #3 and #4).
 *
 * Responsibilities:
 *   - User-gesture commits: handleNewInsert (right-click → Insert),
 *     handleDrawCreateCommit (Path B click-drag-create),
 *     handleDragMoveCommit (multi-select drag),
 *     handleResizeCommit (resize handle drag).
 *   - Snap-to-content geometry guards: bboxCenter, bboxContainsCenter,
 *     bboxesIntersect — pure axis-aligned 2-D primitives.
 *   - Canvas-px → PDF user-space conversion: canvasBboxToPdf.
 *   - Resize reassociation planning: computeResizeReassociatePlan,
 *     absorbToastLabels.
 *
 * Cross-cluster coupling (post-EB.5):
 *   - `buildPageBboxMap` (shared with Cluster D / render) lives in
 *     `editor-bbox-utils.ts`; this module DOES NOT need it directly —
 *     gesture controllers consume it via editor.ts wiring.
 *   - `applyPowerEditorOp`, `rebuildCardLookups`, `rerenderCardList`,
 *     `updateSaveBlockUI`, `showEditorToast` now live in
 *     `editor-card-pane.ts` (Phase EB.5; Cluster F). Phase EB.5 RETIRED
 *     the EB.4 `setInsertFlowCallbackBindings` setter for production
 *     code — those callbacks are now direct imports below. ESM tolerates
 *     the resulting card-pane ↔ insert-flow cycle because both sides
 *     access the other's exports lazily at user-gesture time.
 *     The setter survives as a TEST-ONLY override seam: tests that exercised
 *     the insert-flow commit handlers in isolation (without mounting the
 *     real card pane) call `setInsertFlowCallbackBindings(...)` to swap in
 *     spies. Production never calls the setter — the default bindings are
 *     installed at module-evaluation time from the direct imports.
 *   - `renderOverlays` lives in `editor-render.ts` (already extracted in
 *     Phase EB.2) — direct import, no seam needed.
 */
import { ROLE_UNDEFINED } from "./role-constants.js";
import { buildCards } from "./cards.js";
import { renderOverlays } from "./editor-render.js";
import { detectAbsorbedMcids, detectKickedOutMcids, buildReassociateOp, } from "./mcid-reassociate.js";
import { state } from "./editor-state.js";
import { unprojectRect } from "./page-projection.js";
// editor-card-pane.js direct imports — these populate the production defaults
// for the Cluster F callbacks (post-EB.5 setter retirement). The cycle is
// real but safe: card-pane → insert-flow (for handleNewInsert + canvasBboxToPdf,
// called at user-gesture time) and insert-flow → card-pane (these callbacks,
// also called at user-gesture time). The lazy `_requireInsertFlowCbBindings`
// + module-namespace import shape below resolves the cycle deterministically:
// at module-evaluation time the namespace `_cp` is created BEFORE either
// module's body runs (ESM module-record allocation), and property accesses
// on `_cp` resolve at call time — by which point both modules are fully
// loaded.
import * as _cp from "./editor-card-pane.js";
// Smooth-scroll begins on the next frame; 50 ms is a generous floor over one
// 16 ms frame, ensuring the card is fully scrolled into view before the
// type-chooser popover anchors to it.
const POPOVER_SCROLL_SETTLE_MS = 50;
// Production defaults — sourced from card-pane via namespace import (lazy).
// `null` here means "use production direct-imports"; tests override by
// passing a full bindings object to `setInsertFlowCallbackBindings(...)`.
let _insertFlowCbBindings = null;
const _productionBindings = {
    // Each accessor goes through `_cp.X` so the resolution happens at
    // CALL time, not at module-evaluation time. This is what makes the
    // card-pane ↔ insert-flow ESM cycle deterministic — neither module's
    // exports are read while either is still evaluating its body.
    applyPowerEditorOp: async (op) => _cp.applyPowerEditorOp(op),
    rebuildCardLookups: () => _cp.rebuildCardLookups(),
    rerenderCardList: () => _cp.rerenderCardList(),
    updateSaveBlockUI: () => _cp.updateSaveBlockUI(),
    showEditorToast: (msg, type) => _cp.showEditorToast(msg, type),
};
/**
 * Test-only override seam — swap the Cluster F callbacks for spy mocks.
 *
 * Production code does NOT call this — direct imports from
 * `editor-card-pane.ts` populate the defaults at module-evaluation
 * time. The setter remains for the EB.4 seam tests
 * (`editor-insert-flow.test.mjs`) which exercise the commit handlers
 * in isolation without mounting the real card pane.
 *
 * Phase EB.5 §9.5: this is the documented exception to the
 * "retire the setter entirely" goal — keeping the seam buys cheap test
 * mockability without paying any production runtime cost (production
 * never reaches the setter; the defaults are pre-installed).
 */
export function setInsertFlowCallbackBindings(bindings) {
    _insertFlowCbBindings = bindings;
}
function _requireInsertFlowCbBindings() {
    // Post-EB.5: if no test override is installed, fall through to the
    // production direct-import bindings. The historical fail-fast path
    // is now unreachable.
    return _insertFlowCbBindings ?? _productionBindings;
}
// ─── Insert-flow commit handlers ────────────────────────────────────────────
/**
 * Handle a new UNDEFINED insertion committed by a right-click → Insert action.
 * Adds to the pending store, creates an UNDEFINED card in the local IR,
 * updates the save-block button state.
 *
 * Server-side dispatch (POST /api/editor-edit/<jobId> → MutateKids.AddChild
 * → EditLog) is not yet wired. Inserts buffer locally as Pending; they
 * will flush when the Save flow handles them.
 */
export function handleNewInsert(spec) {
    const cbs = _requireInsertFlowCbBindings();
    // Add to pending store (triggers save-block subscribers).
    // Cast: spec.bbox is `unknown` at the call-site boundary; runtime guarantees BboxXywh shape.
    state.pendingInsertStore.add(spec);
    // Append a local UNDEFINED card so it appears in the card list + SR transcript.
    if (state.ir && Array.isArray(state.ir.blocks)) {
        const newBlock = {
            structId: spec.editorId,
            role: ROLE_UNDEFINED,
            text: "",
            alt: "",
            page: spec.page,
            bbox: spec.bbox,
            undefined: true,
            // Bug 2: when the rubber-band snapped on all four sides the
            // bbox is fully locked to existing structure; resize handles
            // would let the user fight the snap. Forwarded to the overlay
            // renderer via state.snappedAllSidesByStructId so the
            // .editor-overlay-bbox carries the data-locked-snap attribute.
            snappedAllSides: !!spec.snappedAllSides,
        };
        // [DESIGN]+[FIX] follow-up: this UNDEFINED-placeholder block carries
        // fields (`undefined: true`, `snappedAllSides`, `role: ROLE_UNDEFINED`)
        // that don't fit the typed Block union createElementReducer produces.
        // Dispatching via {kind:"create-element",...} would lose those fields
        // (the reducer maps the role onto a typed Block kind — paragraph for
        // ROLE_UNDEFINED — and discards the extras). Until create-element is
        // extended with a `pendingInsertFlags` payload, this site retains the
        // direct write with a same-line noqa per the lint contract.
        state.ir = { ...state.ir, blocks: [...state.ir.blocks, newBlock] }; // noqa: editor-dispatch-monopoly — UNDEFINED placeholder carries pendingInsertStore-coupled fields; [DESIGN] follow-up tracks the create-element extension // noqa: editor-oplog-monopoly — op is deferred, not skipped: the create-element op is pushed on the subsequent set-role that resolves this placeholder (editor-card-pane.ts set-role handling)
        state.cards = buildCards(state.ir);
        cbs.rebuildCardLookups();
        cbs.rerenderCardList();
    }
    cbs.updateSaveBlockUI();
    // Select the freshly-inserted card so it's visually distinct, then open
    // the type-picker on it after the rerender lands. The user just dragged
    // out a region — making them hunt for the "Choose type" button is a
    // discoverability tax. Mockup ref: card-gui §insertUndefinedElement
    // also auto-flashes + selects the new card.
    if (state.linkedViewsCtrl) {
        try {
            state.linkedViewsCtrl.selectStructId(spec.editorId, "single");
        }
        catch { /* best-effort selection — host may have torn down between insert and re-render */ }
    }
    setTimeout(() => {
        const editPaneBody = document.getElementById("pane-editor-body"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        const cardListEl = editPaneBody?.querySelector(".editor-card-list");
        if (!cardListEl)
            return;
        const escaped = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(spec.editorId) : spec.editorId;
        const chooseBtn = cardListEl.querySelector(`.editor-card[data-struct-id="${escaped}"] .editor-card-undef-choose-btn`);
        if (chooseBtn && typeof chooseBtn.click === "function") {
            // Scroll the new card into view first; the popover anchors to the
            // button's bounding box, so it has to be on-screen.
            const cardEl = cardListEl.querySelector(`.editor-card[data-struct-id="${escaped}"]`);
            if (cardEl && typeof cardEl.scrollIntoView === "function") {
                cardEl.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            chooseBtn.click();
        }
    }, POPOVER_SCROLL_SETTLE_MS);
}
// ─── Drag-to-move bbox handling ───────────────────────────────────────────
/**
 * Handle a completed resize commit. Single-element only — resize handles
 * always operate on one bbox at a time. Pushes one set-bbox op (+ inverse
 * for undo).
 *
 * @param {{structId:string, before:BboxArray, after:BboxArray}} spec
 */
export function handleResizeCommit(specIn) {
    if (!specIn?.structId || !Array.isArray(specIn.before) || !Array.isArray(specIn.after))
        return;
    const spec = specIn;
    const cbs = _requireInsertFlowCbBindings();
    // Snap-to-content constraint for resize: the new bbox MUST include the
    // original bbox's center. Resize is for *correcting* a too-small original
    // bbox; the original glyph region (proxied here by the original bbox
    // center, since the IR doesn't carry per-MCID positions) must stay
    // covered. If the user resized so aggressively that the new bbox excludes
    // the original center, snap back instead of committing.
    if (!bboxContainsCenter(spec.after, bboxCenter(spec.before))) {
        cbs.showEditorToast("Resize cancelled — new bbox excludes the element's content region", "warn");
        // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync resize handler; await would require async signature change
        renderOverlays().catch(() => { });
        return;
    }
    // Path B Phase 2 — detect absorb (resize-bigger covers MCIDs from
    // other struct elems) and kick-out (resize-smaller leaves MCIDs
    // outside the new bbox). Emits an additional `reassociate-mcids`
    // op alongside the existing `set-bbox`.
    // Per docs/ux/editor-mcid-reassociation-260505.md §2.
    const reassocPlan = computeResizeReassociatePlan(spec);
    // Phase FB.9 (260524): retired the 260523 async-IIFE-wrap. Per §P.5
    // of docs/design/frontend-mv-architecture-260524.md, BATCH folds N
    // sub-ops sequentially against ONE starting IR — set-bbox (client-only)
    // + absorb/kickout (WASM round-trip) commit as one IR mutation, no
    // race between absorb's and kickout's nextIr computations. Per-op
    // WASM round-trips fire serially after the optimistic commit.
    function applyForward() {
        const forwardOps = [
            { kind: "set-bbox", structId: spec.structId, before: spec.before, after: spec.after },
        ];
        if (reassocPlan?.absorbOp) {
            forwardOps.push(reassocPlan.absorbOp);
        }
        if (reassocPlan?.kickoutOp) {
            forwardOps.push(reassocPlan.kickoutOp);
        }
        void cbs.applyPowerEditorOp({ kind: "BATCH", ops: forwardOps });
    }
    function applyInverse() {
        void cbs.applyPowerEditorOp({
            kind: "set-bbox", structId: spec.structId,
            before: spec.after, after: spec.before,
        });
    }
    // Typed-role absorb proceeds silently. The merge is fully reversible
    // via Cmd-Z (Undo), so a confirmation dialog is friction without
    // safety value. We surface a non-blocking toast naming the absorbed
    // typed elements so the user notices the merge happened, then push
    // an undo entry — same pattern as every other reversible gesture.
    // See docs/ux/editor-philosophy.md.
    const typedAbsorbStructIds = reassocPlan?.typedAbsorbStructIds ?? [];
    if (typedAbsorbStructIds.length > 0) {
        const labels = absorbToastLabels(typedAbsorbStructIds);
        const summary = labels.length === 1
            ? `Absorbed ${labels[0]} — Cmd-Z to undo`
            : `Absorbed ${labels.length} typed elements (${labels.join(", ")}) — Cmd-Z to undo`;
        cbs.showEditorToast(summary, "info");
    }
    applyForward();
    if (state.undoRedo) {
        state.undoRedo.push({ doFn: applyForward, undoFn: applyInverse, label: "resize element" });
    }
    // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync resize handler; await would require async signature change
    renderOverlays().catch(() => { });
}
/**
 * Build human-readable role labels for the absorb toast.
 * Mirrors the previous confirmAbsorbModal labelling logic so the toast
 * conveys the same identifying information without blocking the user.
 *
 * @param {string[]} structIds
 * @returns {string[]}
 */
export function absorbToastLabels(structIds) {
    return structIds.map((id) => {
        const b = (state.ir?.blocks ?? []).find((x) => x?.structId === id);
        return b ? `${b.role} (${id})` : id;
    });
}
/**
 * Plan a resize commit's reassociate-mcids ops.
 *
 * Returns `{ absorbOp, kickoutOp, typedAbsorbStructIds }` where:
 *   - absorbOp: reassociate-mcids op moving newly-covered MCIDs INTO
 *               the resizing struct (resize-bigger).
 *   - kickoutOp: reassociate-mcids op pulling MCIDs OUT of the resizing
 *                struct into a fresh UNDEFINED struct (resize-smaller).
 *   - typedAbsorbStructIds: list of struct ids being absorbed that have
 *                           a typed (non-Artifact, non-UNDEFINED) role,
 *                           triggering the confirmation modal.
 *
 * Returns null when state.ir.mcids is unavailable (e.g. legacy IR).
 */
export function computeResizeReassociatePlan(spec) {
    if (!state.ir || !Array.isArray(state.ir.mcids) || state.ir.mcids.length === 0) {
        return null;
    }
    // Determine the page from the resizing block.
    const block = state.ir.blocks?.find((b) => b?.structId === spec.structId);
    const pageNum = block ? block.page : null;
    if (!pageNum)
        return null;
    const mcidsOnPage = state.ir.mcids.filter((m) => m !== null && m !== undefined && m.page === pageNum);
    if (mcidsOnPage.length === 0)
        return null;
    // Owner lookup — walks the IR blocks to find which struct each MCID
    // currently belongs to. The IR doesn't carry per-block MCID lists
    // explicitly, so we approximate via centroid containment in each
    // block's bbox. This is the same proxy the C# IR uses to derive
    // /BBox in the first place, so the answers are coherent.
    function ownerLookup(mcidPage, mcidNum) {
        const m = mcidsOnPage.find((x) => x.mcid === mcidNum);
        if (!m?.centroid)
            return null;
        const cx = m.centroid.x, cy = m.centroid.y;
        let bestId = null;
        let bestArea = Infinity;
        for (const b of state.ir?.blocks ?? []) {
            if (b?.page !== mcidPage || !b.bbox)
                continue;
            const bb = b.bbox;
            if (cx < bb.x || cx > bb.x + bb.w)
                continue;
            if (cy < bb.y || cy > bb.y + bb.h)
                continue;
            const area = bb.w * bb.h;
            if (area < bestArea) {
                bestArea = area;
                bestId = b.structId;
            }
        }
        return bestId;
    }
    const beforeArea = spec.before[2] * spec.before[3];
    const afterArea = spec.after[2] * spec.after[3];
    const isGrowing = afterArea > beforeArea + 1;
    const isShrinking = afterArea + 1 < beforeArea;
    let absorbOp = null;
    const typedAbsorbStructIds = [];
    if (isGrowing) {
        const { absorbed, byOwner } = detectAbsorbedMcids({
            mcidsOnPage, resizingStructId: spec.structId, ownerLookup,
            oldBbox: spec.before,
            newBbox: spec.after,
        });
        if (absorbed.length > 0) {
            absorbOp = buildReassociateOp({
                kind: "absorb", mcids: absorbed, targetStructId: spec.structId,
            });
            // Detect typed-role absorbs for confirmation modal.
            for (const [ownerId] of byOwner.entries()) {
                if (!ownerId || ownerId === spec.structId)
                    continue;
                const ob = (state.ir?.blocks ?? []).find((b) => b?.structId === ownerId);
                if (!ob)
                    continue;
                const role = String(ob.role ?? "");
                const isTyped = role && role !== ROLE_UNDEFINED && role !== "Artifact";
                if (isTyped)
                    typedAbsorbStructIds.push(ownerId);
            }
        }
    }
    let kickoutOp = null;
    if (isShrinking) {
        const kicked = detectKickedOutMcids({
            mcidsOnPage, resizingStructId: spec.structId, ownerLookup,
            oldBbox: spec.before,
            newBbox: spec.after,
        });
        if (kicked.length > 0) {
            // Tightened residual bbox = union of kicked-out MCID bboxes.
            // Falls back to the difference between old and new bbox bounds.
            const fallback = spec.before;
            const residualBbox = (function unionMcidBboxes() {
                let xMin = Infinity, yMin = Infinity, xMax = -Infinity, yMax = -Infinity;
                for (const m of kicked) {
                    if (!m.bbox)
                        continue;
                    if (m.bbox.x < xMin)
                        xMin = m.bbox.x;
                    if (m.bbox.y < yMin)
                        yMin = m.bbox.y;
                    if (m.bbox.x + m.bbox.w > xMax)
                        xMax = m.bbox.x + m.bbox.w;
                    if (m.bbox.y + m.bbox.h > yMax)
                        yMax = m.bbox.y + m.bbox.h;
                }
                if (!Number.isFinite(xMin))
                    return fallback;
                return [xMin, yMin, xMax - xMin, yMax - yMin];
            })();
            kickoutOp = buildReassociateOp({
                kind: "kick-out", mcids: kicked,
                targetStructId: spec.structId, bbox: residualBbox,
            });
        }
    }
    return { absorbOp, kickoutOp, typedAbsorbStructIds };
}
/**
 * Handle a click-drag-create commit (Path B gesture 3). Builds an
 * UNDEFINED-role create op + a reassociate-mcids create op, registers
 * an UNDEFINED block in the local IR (so the user can pick a role from
 * the card), and pushes both ops to the undo log.
 *
 * @param {number} pageNum
 * @param {{ tightenedBbox: [number,number,number,number], mcids: unknown[] }} spec
 */
export function handleDrawCreateCommit(pageNum, spec) {
    if (!spec || !Array.isArray(spec.tightenedBbox))
        return;
    const cbs = _requireInsertFlowCbBindings();
    const { defaultInsertSize: _ds, nextEditorInsertId } = (arguments.length > 99 ? {} : (() => {
        // dynamic import already happens in handleNewInsert; reuse its
        // editor-id allocator to keep the local-id namespace consistent.
        // Inline a fresh id here — same shape as nextEditorInsertId().
        const ts = Date.now().toString(36);
        const rand = Math.floor(Math.random() * 1e6).toString(36);
        return { nextEditorInsertId: () => `editor-draw-${ts}-${rand}` };
    })());
    const editorId = nextEditorInsertId();
    // The reassociate-mcids op carries the wire-shape (the C# server
    // creates the new struct elem). Simultaneously, register a local
    // UNDEFINED block so the user sees a card + can pick the role —
    // same UX as right-click → Insert + drag-to-insert.
    const reassocOp = buildReassociateOp({
        kind: "create",
        mcids: (spec.mcids ?? []),
        bbox: spec.tightenedBbox,
    });
    // Local insert (mirrors handleNewInsert; the SPA-side IR carries an
    // UNDEFINED block until the user picks a role).
    // Cast: _reassocOp is stashed on the entry as an extension point beyond PendingInsertEntry
    state.pendingInsertStore.add({
        editorId,
        page: pageNum,
        bbox: { x: spec.tightenedBbox[0], y: spec.tightenedBbox[1],
            w: spec.tightenedBbox[2], h: spec.tightenedBbox[3] },
        pdfBbox: spec.tightenedBbox,
        anchorStructId: null,
        // Stash the reassocOp + mcids on the pending entry so the
        // type-resolution flow can emit them as a unit on save.
        _reassocOp: reassocOp,
    });
    if (state.ir && Array.isArray(state.ir.blocks)) {
        const newBlock = {
            structId: editorId,
            role: ROLE_UNDEFINED,
            text: "",
            alt: "",
            page: pageNum,
            bbox: { x: spec.tightenedBbox[0], y: spec.tightenedBbox[1],
                w: spec.tightenedBbox[2], h: spec.tightenedBbox[3] },
            undefined: true,
        };
        // Same UNDEFINED-placeholder pattern as handleNewInsert above; same
        // [DESIGN]+[FIX] follow-up applies (extend create-element to carry
        // pendingInsertStore-coupled fields).
        state.ir = { ...state.ir, blocks: [...state.ir.blocks, newBlock] }; // noqa: editor-dispatch-monopoly — UNDEFINED placeholder carries pendingInsertStore-coupled fields; [DESIGN] follow-up tracks the create-element extension // noqa: editor-oplog-monopoly — op is deferred, not skipped: the create-element op is pushed on the subsequent set-role that resolves this placeholder (editor-card-pane.ts set-role handling)
        state.cards = buildCards(state.ir);
        cbs.rebuildCardLookups();
        cbs.rerenderCardList();
    }
    if (state.linkedViewsCtrl) {
        try {
            state.linkedViewsCtrl.selectStructId(editorId, "single");
        }
        catch { /* best-effort selection */ }
    }
    cbs.updateSaveBlockUI();
    // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync insert handler; await would require async signature change
    renderOverlays().catch(() => { });
    // Auto-open the type-picker on the freshly-inserted card so the user can
    // pick a role without hunting for the "Choose type" button. Same UX as
    // handleNewInsert (drag-to-insert on pages without MCID data).
    setTimeout(() => {
        const editPaneBody = document.getElementById("pane-editor-body"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        const cardListEl = editPaneBody?.querySelector(".editor-card-list");
        if (!cardListEl)
            return;
        const escaped = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(editorId) : editorId;
        const cardEl = cardListEl.querySelector(`.editor-card[data-struct-id="${escaped}"]`);
        if (cardEl && typeof cardEl.scrollIntoView === "function") {
            cardEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        const chooseBtn = cardListEl.querySelector(`.editor-card[data-struct-id="${escaped}"] .editor-card-undef-choose-btn`);
        if (chooseBtn && typeof chooseBtn.click === "function")
            chooseBtn.click();
    }, POPOVER_SCROLL_SETTLE_MS);
}
/**
 * Handle a completed drag-move commit. Pushes one set-bbox op per moved
 * struct id under a single undo-stack frame so Cmd-Z reverts the whole
 * group drag atomically. Each set-bbox carries `before` and `after` as
 * [x, y, w, h] in PDF user-space.
 *
 * @param {{sourceId:string, moves:Array<{structId:string, before:BboxArray, after:BboxArray}>}} spec
 */
export function handleDragMoveCommit(spec) {
    if (!spec || !Array.isArray(spec.moves) || spec.moves.length === 0)
        return;
    const cbs = _requireInsertFlowCbBindings();
    // Snap-to-content constraint for drag-move: each new bbox must intersect
    // the original bbox (proxy for the MCID glyph region the struct elem
    // references). The PDF/UA `/BBox` attribute is independent of the MCID
    // glyphs, so a free-form drag could leave `/BBox` claiming one region
    // while the element still references glyphs in another — incoherent for
    // screen readers that consult `/BBox` for spatial context. Block the
    // drag when no original-bbox intersection survives.
    const moves = spec.moves.filter((m) => bboxesIntersect(m.before, m.after));
    if (moves.length === 0) {
        cbs.showEditorToast("Move cancelled — would relocate elements beyond their content region", "warn");
        // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync drag handler; await would require async signature change
        renderOverlays().catch(() => { });
        return;
    }
    if (moves.length < spec.moves.length) {
        // Partial — some moves snapped back. Surface a soft warning so the
        // user knows not every member of the multi-select drag landed.
        const dropped = spec.moves.length - moves.length;
        cbs.showEditorToast(`${dropped} ${dropped === 1 ? "element" : "elements"} stayed put — would relocate beyond content region`, "warn");
    }
    function applyForward() {
        for (const m of moves) {
            void cbs.applyPowerEditorOp({
                kind: "set-bbox",
                structId: m.structId,
                before: m.before,
                after: m.after,
            });
        }
    }
    function applyInverse() {
        for (const m of moves) {
            void cbs.applyPowerEditorOp({
                kind: "set-bbox",
                structId: m.structId,
                before: m.after,
                after: m.before,
            });
        }
    }
    applyForward();
    if (state.undoRedo) {
        state.undoRedo.push({
            doFn: applyForward,
            undoFn: applyInverse,
            label: moves.length > 1 ? `move ${moves.length} elements` : "move element",
        });
    }
    // Re-render overlays so the new bbox positions take effect.
    // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync drag handler; await would require async signature change
    renderOverlays().catch(() => { });
}
// ─── Bbox geometry primitives ───────────────────────────────────────────────
/**
 * Return [centerX, centerY] for an [x, y, w, h] bbox in PDF user-space.
 * Used by the drag/resize snap-to-content constraint.
 */
export function bboxCenter(b) {
    if (!Array.isArray(b) || b.length !== 4)
        return [0, 0];
    return [b[0] + b[2] / 2, b[1] + b[3] / 2];
}
/**
 * True iff the supplied [cx, cy] point lies within bbox [x, y, w, h].
 * Boundary inclusive.
 */
export function bboxContainsCenter(b, center) {
    if (!Array.isArray(b) || b.length !== 4)
        return false;
    if (!Array.isArray(center) || center.length !== 2)
        return false;
    const [cx, cy] = center;
    return cx >= b[0] && cx <= b[0] + b[2] && cy >= b[1] && cy <= b[1] + b[3];
}
/**
 * True iff axis-aligned bboxes a and b have non-zero overlap. Used to gate
 * drag-move: a freshly-dragged bbox must still cover part of the original
 * bbox (proxy for the glyph region the struct elem MCID-references).
 */
export function bboxesIntersect(a, b) {
    if (!Array.isArray(a) || a.length !== 4)
        return false;
    if (!Array.isArray(b) || b.length !== 4)
        return false;
    return !(a[0] + a[2] <= b[0] || b[0] + b[2] <= a[0]
        || a[1] + a[3] <= b[1] || b[1] + b[3] <= a[1]);
}
/**
 * Convert a canvas-px bbox (origin top-left, y-down) to PDF user-space
 * (origin bottom-left, y-up) for the supplied 1-based page. Returns
 * a [x, y, w, h] array.
 *
 * The inverse of the overlay projection — un-projects through the SAME
 * `PageProjection` (the pdf.js viewport.transform carried on the descriptor)
 * that overlays project through, so a box drawn on a rotated or cropped page
 * saves to the correct user-space coordinates (design §4.1 — the inverse must
 * migrate in lockstep, or draw-to-create re-diverges from the overlay). No DOM
 * dataset re-read — that crop/rotation-blind inverse was bug-2's root on the
 * draw path. If the page hasn't been painted (no projection) the canvas-px
 * coords are returned verbatim — better than failing the insert outright.
 */
export function canvasBboxToPdf(canvasRect, pageNum) {
    const projection = state.pageDescriptors[pageNum - 1]?.projection;
    if (!projection) {
        return [canvasRect.x, canvasRect.y, canvasRect.w, canvasRect.h];
    }
    return unprojectRect(canvasRect, projection);
}
